import { findModelForEditing, submitServerChange } from 'components/dataCreationForms/common';
import {
  EditContext,
  OpenEditDialogParams,
} from 'components/dataCreationForms/types';
import { makeHandleFormValidation } from 'components/dataCreationForms/validation';
import { useSites } from 'store/site';
import { useCancelConfirmation } from 'presentation/dialogForms/dialogFormsHooks';
import {
  ComponentType,
  ReactElement,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useIntl } from 'react-intl';
import {
  useAppUrlParams,
  UrlParamName,
} from 'hooks/navigation/useAppUrlParams';
import { useProcesses } from 'store/process';
import { useProject } from 'store/project';
import { getProjectUsersStoreState, useProjectUsers } from 'store/projectUsers';
import { userEditOnViewToUserEditModel } from 'shared/domain/user/mapping/toModel';
import { userModelToUserEditOnView } from 'shared/domain/user/mapping/toView';
import { UserEditOnView } from 'shared/domain/user/types/view';
import { userClient } from 'shared/domain/user/userClient';
import { CreatableUserRole, UserRole } from 'shared/types/userRole';
import { editUserValidationSchema, validateEditUser } from '../validation';
import { useAppNavigation } from 'contexts/navigation';
const formName = 'user';
const SUBMIT_EVENT_TYPE = 'submit-user';
const WithEditUserContext = createContext<
  EditContext<UserEditOnView> | undefined
>(undefined);

function WithEditUser({
  children,
  onSuccessCallback,
}: {
  children: ReactElement;
  onSuccessCallback?: (_id: string, label: string) => void;
}): ReactElement {
  const { items: users } = useProjectUsers();
  const intl = useIntl();
  const project = useProject();
  const projectId = project._id;
  const organizationId = project.organizationId;
  const {
    items: sites,
    loading: loadingSites,
  } = useSites();
  const { processes } = useProcesses();
  const [isPosting, setIsPosting] = useState<boolean>(false);
  const releaseSubmitEvent = useCallback(() => {
    window.dispatchEvent(new CustomEvent(SUBMIT_EVENT_TYPE));
  }, []);
  const { goToUserDetails } = useAppNavigation();
  const [currentUserId, setCurrentUserId] = useState(
    useAppUrlParams(UrlParamName.USER_ID)
  );

  const [existingValues, setExistingValues] = useState<
    UserEditOnView | undefined
  >(undefined);

  useEffect(() => {
    const existingUserModel = findModelForEditing(users, currentUserId);
    let userOnView: UserEditOnView;
    if (existingUserModel) {
      userOnView = userModelToUserEditOnView({
        userModel: existingUserModel,
        sites,
        processes,
        intl,
        organizationId,
      });
      setExistingValues(userOnView);
    }
  }, [users, intl, currentUserId, processes, sites]);

  const [open, setOpen] = useState(false);

  const handleFormValidation = useMemo(() => {
    return makeHandleFormValidation(editUserValidationSchema);
  }, []);
  const openDialog = useCallback(({ _id }: OpenEditDialogParams) => {
    setOpen(true);
    setCurrentUserId(_id);
  }, []);
  const onSuccess = useCallback(
    (_id: string, label: string) =>
      onSuccessCallback?.(_id, label),
    [onSuccessCallback]
  );
  const confirmCancel = useCancelConfirmation(formName);
  const openConfirmationDialog = useCallback(async () => {
    const { cancelConfirmed } = await confirmCancel();
    if (!cancelConfirmed) {
      return;
    }
    setOpen(false);
  }, [confirmCancel]);

  const submitForm = useCallback(
    async (values: UserEditOnView) => {
      const userEditModel = userEditOnViewToUserEditModel({
        userEditOnView: values,
        projectId,
        organizationId,
        previousValues: {
          role:
            (existingValues?.role._id!) ||
            UserRole.standard,
          sites: existingValues?.sites || [],
          processes: existingValues?.processes || [],
          label: existingValues?.label || '',
          phone: existingValues?.phone || '',
        },
      });
      const hasChanges =
        userEditModel.permissions ||
        typeof userEditModel.label === 'string' ||
        typeof userEditModel.phone === 'string';

      if (!hasChanges) {
        goToUserDetails(userEditModel._id);
        return;
      }

      const { saved } = await submitServerChange({
        change: () => userClient.edit(userEditModel),
        setOpen,
        setIsPosting,
        action: 'updating a user',
      });
      if (!saved) {
        return;
      }

      // The details view shows the user from the store.
      await getProjectUsersStoreState().actions.reload();
      goToUserDetails(userEditModel._id);
    },
    [
      projectId,
      organizationId,
      existingValues?.sites,
      existingValues?.processes,
      existingValues?.role,
      existingValues?.label,
      existingValues?.phone,
      goToUserDetails,
    ]
  );

  const ctx: EditContext<UserEditOnView> = useMemo(() => {
    return {
      open,
      openDialog,
      closeDialog: openConfirmationDialog,
      submitForm,
      isPosting,
      existingValues,
      SUBMIT_EVENT_TYPE,
      releaseSubmitEvent,
      validate: validateEditUser,
      handleFormValidation,
      onSuccess,
      formName,
    };
  }, [
    openConfirmationDialog,
    existingValues,
    isPosting,
    open,
    openDialog,
    releaseSubmitEvent,
    submitForm,
    onSuccess,
    handleFormValidation,
  ]);

  return (
    <WithEditUserContext.Provider value={ctx}>
      {children}
    </WithEditUserContext.Provider>
  );
}

function useEditUser(): EditContext<UserEditOnView> {
  const context = useContext(WithEditUserContext);
  if (context === undefined) {
    throw new Error('useEditUser must be used within an EditUserContext');
  }
  return context;
}

const withEditUser =
  (Component: ComponentType<any>) =>
  ({ ...props }): ReactElement => (
    <WithEditUser>
      <Component {...props} />
    </WithEditUser>
  );

export { WithEditUser, useEditUser, withEditUser };
